
type ThemeColors = {
  primaryColor?: string;
  lightColor?: string;
  lightGradientColor?: string;
  secondaryColor?: string;
  grayDisabled?: string;
  yellowEnergyColor?: string;
  errorColor?: string;
  grey1?: string;
  grey2?: string;
  grey3?: string;
  grey4?: string;
  blackColor?: string;
  whiteColor?: string;
  yellowAlertColor?: string;
  primaryGradColor?: string;
  bright?: string;
  primaryBtn?: string;
  primaryBtnGradient?: string;
};

function extractColorsFromStartPage(settings: any): ThemeColors {
  const getValue = (propName: string) => {
    let prop = settings[propName];
    if (!prop) {
      const pascalCase = propName.charAt(0).toUpperCase() + propName.slice(1);
      prop = settings[pascalCase];
    }
    if (!prop) return undefined;
    if (prop.value !== undefined) return prop.value;
    return prop;
  };

  // Keys match Content API / CmsHomePageData exactly (camelCase).
  return {
    primaryColor: getValue('primaryColor'),
    lightColor: getValue('lightColor'),
    lightGradientColor: getValue('lightGradientColor'),
    secondaryColor: getValue('secondaryColor'),
    grayDisabled: getValue('grayDisabled'),
    yellowEnergyColor: getValue('yellowEnergyColor'),
    errorColor: getValue('errorColor'),
    grey1: getValue('grey1'),
    grey2: getValue('grey2'),
    grey3: getValue('grey3'),
    grey4: getValue('grey4'),
    blackColor: getValue('blackColor'),
    whiteColor: getValue('whiteColor'),
    yellowAlertColor: getValue('yellowAlertColor'),
    primaryGradColor: getValue('primaryGradColor'),
    bright: getValue('bright'),
    primaryBtn: getValue('primaryBtn'),
    primaryBtnGradient: getValue('primaryBtnGradient'),
  };
}

/** Injects `<style id="cms-theme-colors">` with :root variables and utility overrides. */
export function applyThemeColors(colors: ThemeColors) {
  let styleElement = document.getElementById('cms-theme-colors');

  if (!styleElement) {
    styleElement = document.createElement('style');
    styleElement.id = 'cms-theme-colors';
    document.head.appendChild(styleElement);
  }

  const cssRules: string[] = [':root {'];

  if (colors.primaryColor) {
    cssRules.push(`  --theme-primary: ${colors.primaryColor};`);
  }
  if (colors.lightColor) {
    cssRules.push(`  --theme-light: ${colors.lightColor};`);
  }
  if (colors.lightGradientColor) {
    cssRules.push(`  --theme-light-grad: ${colors.lightGradientColor};`);
  }
  if (colors.secondaryColor) {
    cssRules.push(`  --theme-secondary: ${colors.secondaryColor};`);
  }
  if (colors.grayDisabled) {
    cssRules.push(`  --theme-gray-disabled: ${colors.grayDisabled};`);
  }
  if (colors.yellowEnergyColor) {
    cssRules.push(`  --theme-yellow-energy: ${colors.yellowEnergyColor};`);
  }
  if (colors.errorColor) {
    cssRules.push(`  --theme-error: ${colors.errorColor};`);
  }
  if (colors.grey1) {
    cssRules.push(`  --theme-grey-1: ${colors.grey1};`);
  }
  if (colors.grey2) {
    cssRules.push(`  --theme-grey-2: ${colors.grey2};`);
  }
  if (colors.grey3) {
    cssRules.push(`  --theme-grey-3: ${colors.grey3};`);
  }
  if (colors.grey4) {
    cssRules.push(`  --theme-grey-4: ${colors.grey4};`);
  }
  if (colors.blackColor) {
    cssRules.push(`  --theme-black: ${colors.blackColor};`);
  }
  if (colors.whiteColor) {
    cssRules.push(`  --theme-white: ${colors.whiteColor};`);
  }
  if (colors.yellowAlertColor) {
    cssRules.push(`  --theme-yellow-alert: ${colors.yellowAlertColor};`);
  }
  if (colors.primaryGradColor) {
    cssRules.push(`  --theme-primary-grad: ${colors.primaryGradColor};`);
  }
  if (colors.bright) {
    cssRules.push(`  --theme-bright: ${colors.bright};`);
  }
  if (colors.primaryBtn) {
    cssRules.push(`  --theme-primary-btn: ${colors.primaryBtn};`);
  }
  if (colors.primaryBtnGradient) {
    cssRules.push(`  --theme-primary-gradient-btn: ${colors.primaryBtnGradient};`);
  }
  cssRules.push('}');

  if (colors.primaryColor) {
    cssRules.push(`
/* Primary Color Overrides */
.btn-primary, .bg-primary { background-color: ${colors.primaryColor} !important; }
.text-primary, a.text-primary { color: ${colors.primaryColor} !important; }
.border-primary { border-color: ${colors.primaryColor} !important; }
`);
  }

  if (colors.secondaryColor) {
    cssRules.push(`
/* Secondary Color Overrides */
.btn-secondary, .bg-secondary { background-color: ${colors.secondaryColor} !important; }
.text-secondary, a.text-secondary { color: ${colors.secondaryColor} !important; }
.border-secondary { border-color: ${colors.secondaryColor} !important; }
`);
  }

  if (colors.errorColor) {
    cssRules.push(`
/* Error/Danger Color Overrides */
.btn-danger, .bg-danger { background-color: ${colors.errorColor} !important; }
.text-danger, .text-error { color: ${colors.errorColor} !important; }
.border-danger, .border-error { border-color: ${colors.errorColor} !important; }
`);
  }

  if (colors.grey1) {
    cssRules.push(`
/* Grey 1 (Border) Color Overrides */
.border, [class*="border-"] { border-color: ${colors.grey1} !important; }
`);
  }

  styleElement.textContent = cssRules.join('\n');
}

/**
 * Applies CMS theme CSS before the first child paint when called from ThemeLoader render
 * (ThemeLoader is mounted before Provider / page content). useEffect runs too late for
 * code that reads :root variables during the same render pass (e.g. svg-compose svg()).
 */
export function syncApplyCmsThemeFromStartPage(startPage: unknown): void {
  if (typeof window === 'undefined' || !startPage || !document?.documentElement) {
    return;
  }
  try {
    const colors = extractColorsFromStartPage(startPage as any);
    applyThemeColors(colors);
  } catch (err) {
    console.error('[syncApplyCmsThemeFromStartPage] Failed to apply theme colors:', err);
  }
}

